/**
 * Route Constants - Centralized routing configuration
 * Maps all application routes dynamically for easy maintenance and type safety
 */

// ============================================================================
// INTERNAL ROUTES - Pages within the application
// ============================================================================

/**
 * Main application routes based on Next.js App Router structure
 */
export const APP_ROUTES = {
  HOME: '/',
  ABOUT_US: '/about-lips',
  AESTHETIC: '/specialities/aesthetics-clinic',
  AUDIOLOGY: '/specialities/audiology-hearing-test-battersea-london',
  PHYSIOTHERAPY: '/physiotherapy',
  FIBRO_SCAN: '/specialities/liver-health-fibroscan',
  PRIVATE_HEALTH_PACKAGES: '/health-packages',
} as const;

/**
 * Service routes - for future service pages
 */
export const SERVICE_ROUTES = {
  SERVICES: '/services',
  GENERAL_PRACTICE: '/services/general-practice',
  DENTAL: '/services/dental',
  PHYSIOTHERAPY: '/services/physiotherapy',
  BLOOD_TESTS: '/services/blood-tests',
  RADIOLOGY: '/services/radiology',
  CARDIOLOGY: '/services/cardiology',
  DERMATOLOGY: '/services/dermatology',
  ORTHOPEDIC: '/services/orthopedic',
  GYNECOLOGY: '/services/gynecology',
  PEDIATRICS: '/services/pediatrics',
} as const;

/**
 * Specialist routes - for specialist profiles and listings
 */
export const SPECIALIST_ROUTES = {
  SPECIALISTS: '/our-specialists',
  CARDIOLOGIST: '/our-specialists/cardiologist',
  DERMATOLOGIST: '/our-specialists/dermatologist',
  ORTHOPEDIC: '/our-specialists/orthopedic',
  GYNECOLOGIST: '/our-specialists/gynecologist',
  PEDIATRICIAN: '/our-specialists/pediatrician',
  OUR_SPECIALISTS: '/our-specialists', // With pagination: ?page=1
} as const;

/**
 * Legal and policy pages
 */
export const LEGAL_ROUTES = {
  PRIVACY_POLICY: '/privacy-policy',
  TERMS_CONDITIONS: '/terms-and-conditions',
  COOKIE_POLICY: '/cookies',
  COMPLAINTS_POLICY: '/complaint-policy',
  CANCELLATION_POLICY: '/cancellation-policy',
} as const;

/**
 * Contact and information routes
 */
export const INFO_ROUTES = {
  CONTACT: '/contact-us',
  FAQ: '/faq',
  BLOG: '/blog',
  NEWS: '/news',
  CAREERS: '/careers',
  LEAVE_FEEDBACK: '/feedback',
  PRICING: '/pricing',
} as const;

/**
 * Patient information routes
 */
export const PATIENT_ROUTES = {
  PATIENT_INFO: '/new-patient-information',
  APPOINTMENTS: '/appointments',
  BOOK_APPOINTMENT: '/book-appointment',
  OVERSEAS_PATIENTS: '/overseas-patients',
  INSURANCE: '/insurance',
  PAYMENT_OPTIONS: '/pricing-and-payment',
} as const;

/**
 * Pharmacy routes
 */
export const PHARMACY_ROUTES = {
  PHARMACY: '/pharmacy',
  PRESCRIPTIONS: '/pharmacy/prescriptions',
  PHARMACY_SERVICES: '/pharmacy/services',
} as const;

// ============================================================================
// EXTERNAL ROUTES - Third-party services and platforms
// ============================================================================

/**
 * External service links
 */
export const EXTERNAL_LINKS = {
  // E-commerce
  STORE: 'https://store.lips.org.uk/',

  // Payment financing
  CHRYSALIS_FINANCE:
    'https://cfl-retailer.chrysalisfinance.com/lips-healthcare-4402',

  // Accreditations
  CQC: 'https://www.cqc.org.uk/location/1-19680665038',
  CYBER_ESSENTIALS: 'https://www.ncsc.gov.uk/cyberessentials/overview',

  // Review/Rating platforms
  TOP_DOCTORS: 'https://www.topdoctors.co.uk/gh/lips-healthcare/',
  DOCTIFY: 'https://www.doctify.com/uk/practice/lips-battersea-power-station',
  TRUSTPILOT:
    'https://uk.trustpilot.com/review/lips.org.uk?utm_medium=trustbox&utm_source=Mini',

  // Media coverage - Critical: Between Life and Death
  NETFLIX_CRITICAL:
    'https://www.netflix.com/us/title/81731799?s=a&trkid=13747225&trg=wha&vlang=en&clip=82047572',
  BBC_CRITICAL: 'https://www.bbc.co.uk/news/articles/c3r90lxqj1lo',
  TIMES_CRITICAL:
    'https://www.thetimes.com/culture/tv-radio/article/critical-between-life-and-death-review-medical-emergencies-become-hypnotic-vp8rk33xr',
  MIRROR_CRITICAL:
    'https://www.mirror.co.uk/tv/tv-news/netflix-fans-in-tears-over-35613390?service=responsive',
  STANDARD_CRITICAL:
    'https://www.standard.co.uk/news/health/netflix-documentary-critical-emergency-doctors-paramedics-london-b1170797.html',
  DAILY_MAIL_CRITICAL:
    'https://www.dailymail.co.uk/tv/best-shows/article-14896455/Critical-Between-Life-And-Death.html',
  METRO_CRITICAL:
    'https://metro.co.uk/video/critical-life-death-official-trailer-3464753/',

  // Social Media
  FACEBOOK: 'https://www.facebook.com/lipshealthcare',
  INSTAGRAM: 'https://www.instagram.com/lips.healthcare/',
  LINKEDIN:
    'https://www.linkedin.com/company/london-international-patient-services-lips-?trk=biz-companies-cym',
  TWITTER: 'https://twitter.com/lipshealthcare',
  YOUTUBE: 'https://www.youtube.com/channel/UCwu77wtwiRdsc2ttdj4muBA',
  TIKTOK: 'https://www.tiktok.com/@lipshealthcare.london?_r=1',

  // Pharmacy and prescription services
  ONLINE_STORE: 'https://store.lips.org.uk',

  // Rate us on platforms
  DOCTIFY_RATE:
    'https://www.doctify.com/uk/practice/lips-battersea-power-station',
  TOP_DOCTORS_RATE: 'https://www.topdoctors.co.uk/gh/lips-healthcare/',
} as const;

/**
 * External link type for type safety
 */
export const RATE_US_ON = [
  {
    platform: 'Doctify',
    href: EXTERNAL_LINKS.DOCTIFY_RATE,
    icon: 'doctify',
    imageUrl: 'doctify-logo-1.webp',
  },
  {
    platform: 'Top Doctors',
    href: EXTERNAL_LINKS.TOP_DOCTORS_RATE,
    icon: 'top-doctors',
    imageUrl: '/images/top-doctors-logo.png',
  },
] as const;

/**
 * Social media platforms configuration
 */
export const SOCIAL_MEDIA_PLATFORMS = [
  { platform: 'Facebook', href: EXTERNAL_LINKS.FACEBOOK, icon: 'facebook' },
  { platform: 'Instagram', href: EXTERNAL_LINKS.INSTAGRAM, icon: 'instagram' },
  { platform: 'LinkedIn', href: EXTERNAL_LINKS.LINKEDIN, icon: 'linkedin' },
  // { platform: 'Twitter', href: EXTERNAL_LINKS.TWITTER, icon: 'twitter' },
  { platform: 'YouTube', href: EXTERNAL_LINKS.YOUTUBE, icon: 'youtube' },
  { platform: 'TikTok', href: EXTERNAL_LINKS.TIKTOK, icon: 'tiktok' },
] as const;

// ============================================================================
// ROUTE GROUPS - Organized collections of routes
// ============================================================================

/**
 * All internal routes combined
 */
export const INTERNAL_ROUTES = {
  ...APP_ROUTES,
  ...SERVICE_ROUTES,
  ...SPECIALIST_ROUTES,
  ...LEGAL_ROUTES,
  ...INFO_ROUTES,
  ...PATIENT_ROUTES,
  ...PHARMACY_ROUTES,
} as const;

/**
 * All routes combined (internal + external)
 */
export const ALL_ROUTES = {
  ...INTERNAL_ROUTES,
  ...EXTERNAL_LINKS,
} as const;

// ============================================================================
// ROUTE UTILITIES
// ============================================================================

/**
 * Check if a URL is external
 */
export const isExternalUrl = (url: string): boolean => {
  return url.startsWith('http://') || url.startsWith('https://');
};

/**
 * Get route with query parameters
 */
export const getRouteWithParams = (
  route: string,
  params?: Record<string, string | number>,
): string => {
  if (!params) return route;

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });

  const queryString = searchParams.toString();
  return queryString ? `${route}?${queryString}` : route;
};

/**
 * Get specialist route with pagination
 */
export const getSpecialistsRoute = (page: number = 1): string => {
  return getRouteWithParams(SPECIALIST_ROUTES.OUR_SPECIALISTS, { page });
};

/**
 * Build dynamic service route
 */
export const getServiceRoute = (slug: string): string => {
  return `/services/${slug}`;
};

/**
 * Build dynamic specialist route
 */
export const getSpecialistRoute = (slug: string): string => {
  return `/our-specialists/${slug}`;
};

/**
 * Build dynamic blog/news route
 */
export const getBlogPostRoute = (slug: string): string => {
  return `/blog/${slug}`;
};

/**
 * Get external link attributes for safe external navigation
 */
export const getExternalLinkProps = () => ({
  target: '_blank',
  rel: 'noopener noreferrer',
});

/**
 * Route type definitions for type safety
 */
export type AppRoute = (typeof APP_ROUTES)[keyof typeof APP_ROUTES];
export type ServiceRoute = (typeof SERVICE_ROUTES)[keyof typeof SERVICE_ROUTES];
export type SpecialistRoute =
  (typeof SPECIALIST_ROUTES)[keyof typeof SPECIALIST_ROUTES];
export type LegalRoute = (typeof LEGAL_ROUTES)[keyof typeof LEGAL_ROUTES];
export type InfoRoute = (typeof INFO_ROUTES)[keyof typeof INFO_ROUTES];
export type PatientRoute = (typeof PATIENT_ROUTES)[keyof typeof PATIENT_ROUTES];
export type PharmacyRoute =
  (typeof PHARMACY_ROUTES)[keyof typeof PHARMACY_ROUTES];
export type InternalRoute =
  (typeof INTERNAL_ROUTES)[keyof typeof INTERNAL_ROUTES];
export type ExternalLink = (typeof EXTERNAL_LINKS)[keyof typeof EXTERNAL_LINKS];
export type RouteType = InternalRoute | ExternalLink;

// ============================================================================
// ROUTE METADATA - For SEO and navigation labels
// ============================================================================

/**
 * Route metadata for navigation menus and SEO
 */
export const ROUTE_METADATA: Record<
  string,
  { title: string; description?: string }
> = {
  [APP_ROUTES.HOME]: {
    title: 'Home',
    description: 'LIPS Healthcare - Private Healthcare Services',
  },
  [APP_ROUTES.ABOUT_US]: {
    title: 'About Us',
    description: 'Learn more about LIPS Healthcare',
  },
  [APP_ROUTES.AESTHETIC]: {
    title: 'Aesthetic',
    description: 'Aesthetic services and treatments',
  },
  [APP_ROUTES.PHYSIOTHERAPY]: {
    title: 'Physiotherapy',
    description: 'Expert rehabilitation and movement therapy',
  },
  [SERVICE_ROUTES.SERVICES]: {
    title: 'Services',
    description: 'Our comprehensive healthcare services',
  },
  [SPECIALIST_ROUTES.SPECIALISTS]: {
    title: 'Specialists',
    description: 'Meet our medical specialists',
  },
  [INFO_ROUTES.CONTACT]: {
    title: 'Contact Us',
    description: 'Get in touch with LIPS Healthcare',
  },
  [LEGAL_ROUTES.PRIVACY_POLICY]: {
    title: 'Privacy Policy',
    description: 'Our privacy policy and data protection',
  },
  [LEGAL_ROUTES.TERMS_CONDITIONS]: {
    title: 'Terms & Conditions',
    description: 'Terms and conditions of service',
  },
} as const;

/**
 * Get route metadata
 */
export const getRouteMetadata = (route: string) => {
  return ROUTE_METADATA[route] || { title: route, description: '' };
};
